/**
 * Governance Documents - Governance Maturity Assessment
 *
 * Per design doc: Organizational_Level_Features_Design.md
 * Users UPLOAD governance policy documents at the organization level.
 * Documents are analyzed for maturity to calculate Governance Maturity score.
 *
 * Key features:
 * - Upload policy documents (text content)
 * - FAIR pillar selection per document (with category-based defaults)
 * - Multi-select for batch analysis
 * - Automatic maturity scoring based on content analysis
 * - Gap identification for missing policy categories
 * - View Governance Maturity score and maturity level
 *
 * @version 3.0.0
 * @date 2025-12-05
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './governance-layout.css';
import './organizational-interviews.css'; // Reuse multi-select styles
import { DocumentDetailPanel } from './DocumentDetailPanel';
import { ScoreGauge } from '../../components/vendor/ScoreGauge';
import { DomainSelect } from '../../components/triangulation/DomainSelect';

// FAIR pillar flags
interface FairPillars {
  findable: boolean;
  accessible: boolean;
  interoperable: boolean;
  reusable: boolean;
}

// Pillar scores (null if pillar not evaluated)
interface PillarScores {
  F: number | null;
  A: number | null;
  I: number | null;
  R: number | null;
}

// Document type from API
interface GovernanceDocument {
  document_id: string;
  document_name: string;
  document_type: string;
  category: string;
  version: string | null;
  maturity_score: number | null;
  completeness_status: 'complete' | 'partial' | 'missing' | null;
  fair_pillars: FairPillars;
  pillar_scores: PillarScores | null;
  analysis_version: string | null;
  uploaded_at: string;
  analyzed_at: string | null;
  // DC-1666 (v2.5) — opted in as an internal citation source
  include_in_assessment_citations?: boolean;
}

// FAIR sub-rule coverage from API
interface FairSubRuleCoverage {
  id: string;
  name: string;
  pillar: string;
  description: string;
  status: 'covered' | 'partial' | 'missing' | 'not_evaluated';
  evidence_count: number;
  accepted_count: number;
}

// Aggregate stats from API
interface DocumentAggregate {
  total_documents: number;
  analyzed_documents: number;
  // NEW: Actual FAIR score based on evidence
  fair_score: number | null;
  fair_percentage: number | null;
  fair_coverage: FairSubRuleCoverage[];
  selected_pillars: { F: boolean; A: boolean; I: boolean; R: boolean };
  // DEPRECATED: Keyword-based maturity (kept for backward compatibility)
  governance_maturity_score: number | null;
  maturity_level: string;
  categories: string[];
  gaps: string[];
  last_updated: string | null;
}

// Category definitions
const DOCUMENT_CATEGORIES = [
  { id: 'data_quality', label: 'Data Quality', icon: 'Q' },
  { id: 'data_access', label: 'Data Access', icon: 'A' },
  { id: 'data_lifecycle', label: 'Data Lifecycle', icon: 'L' },
  { id: 'metadata', label: 'Metadata Standards', icon: 'M' },
  { id: 'privacy', label: 'Privacy & Consent', icon: 'P' },
  { id: 'retention', label: 'Retention Policy', icon: 'R' },
  { id: 'security', label: 'Security', icon: 'S' },
  { id: 'group_transcript', label: 'Group Session Transcript', icon: 'G' },
  { id: 'other', label: 'Other', icon: 'O' },
];

// Document type definitions
const DOCUMENT_TYPES = [
  { id: 'policy', label: 'Policy' },
  { id: 'procedure', label: 'Procedure' },
  { id: 'guideline', label: 'Guideline' },
  { id: 'standard', label: 'Standard' },
  { id: 'other', label: 'Other' },
];

// Category-to-Pillar default mappings (matches server-side)
const CATEGORY_PILLAR_DEFAULTS: Record<string, FairPillars> = {
  data_quality: { findable: false, accessible: false, interoperable: false, reusable: true },
  data_access: { findable: false, accessible: true, interoperable: false, reusable: false },
  data_lifecycle: { findable: false, accessible: false, interoperable: false, reusable: true },
  metadata: { findable: true, accessible: false, interoperable: true, reusable: false },
  privacy: { findable: false, accessible: true, interoperable: false, reusable: true },
  retention: { findable: false, accessible: false, interoperable: false, reusable: true },
  security: { findable: false, accessible: true, interoperable: false, reusable: false },
  group_transcript: { findable: true, accessible: true, interoperable: true, reusable: true },
  other: { findable: true, accessible: true, interoperable: true, reusable: true },
};

// Lens result summary for grid display (DC-1041)
interface LensResultSummary {
  overall_score: number | null;
  lenses_run: number;
  total_lenses: number;
  analyzed_at: string | null;
}

export const GovernanceDocuments: React.FC = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<GovernanceDocument[]>([]);
  const [aggregate, setAggregate] = useState<DocumentAggregate | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Lens score state (DC-1041)
  const [lensResults, setLensResults] = useState<Record<string, LensResultSummary>>({});
  const [runningLenses, setRunningLenses] = useState<Set<string>>(new Set());

  // Multi-select state
  const [selectedDocuments, setSelectedDocuments] = useState<Set<string>>(new Set());
  const [exporting, setExporting] = useState(false);

  // Document governance (owner) status
  const [governanceStatuses, setGovernanceStatuses] = useState<Record<string, { governance_status: string; owner_count: number; owner_names: string | null }>>({});
  const [selectedDocumentForPanel, setSelectedDocumentForPanel] = useState<GovernanceDocument | null>(null);

  // Upload form state
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [documentName, setDocumentName] = useState('');
  const [documentType, setDocumentType] = useState('policy');
  const [category, setCategory] = useState('');
  const [documentText, setDocumentText] = useState('');
  const [version, setVersion] = useState('');
  // FAIR pillar selection
  const [evalFindable, setEvalFindable] = useState(false);
  const [evalAccessible, setEvalAccessible] = useState(false);
  const [evalInteroperable, setEvalInteroperable] = useState(false);
  const [evalReusable, setEvalReusable] = useState(false);
  // DC-1666 (v2.5) — opt this document in to internal assessment citations.
  // Off by default; only opted-in documents are clause-extracted and may be
  // cited beside DQ/ML scores (after admin approval of the mapping).
  const [includeInCitations, setIncludeInCitations] = useState(false);
  // DC-1493 — optional triangulation domain assignment at upload time.
  const [domainId, setDomainId] = useState<string | null>(null);
  // v2.0 (§3.7) — non-blocking extraction-quality notice from /api/upload-pdf
  // (scannedLikely / truncated). Distinct from `error`: it warns, never blocks.
  const [uploadNotice, setUploadNotice] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Update pillar defaults when category changes
  useEffect(() => {
    if (category) {
      const defaults = CATEGORY_PILLAR_DEFAULTS[category] || CATEGORY_PILLAR_DEFAULTS.other;
      setEvalFindable(defaults.findable);
      setEvalAccessible(defaults.accessible);
      setEvalInteroperable(defaults.interoperable);
      setEvalReusable(defaults.reusable);
    }
  }, [category]);

  // Load documents on mount
  useEffect(() => {
    loadDocuments();
  }, []);

  // Open user guide in Typora/default markdown viewer
  const handleOpenGuide = async () => {
    try {
      await fetch('/open-guide', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guidePath: 'Governance_Documents_User_Guide.md' })
      });
    } catch (err) {
      console.error('[GovernanceDocuments] Error opening guide:', err);
    }
  };

  const loadDocuments = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/organization/documents');
      if (!response.ok) {
        throw new Error(`Failed to load documents: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success) {
        const docs = data.documents || [];
        setDocuments(docs);
        setAggregate(data.aggregate || null);

        // Fetch governance statuses for all documents
        if (docs.length > 0) {
          const docIds = docs.map((d: GovernanceDocument) => d.document_id);
          try {
            const govResponse = await fetch(`/api/organization/documents/governance/batch?document_ids=${docIds.join(',')}`);
            if (govResponse.ok) {
              const govData = await govResponse.json();
              if (govData.success && govData.statuses) {
                const statusMap: Record<string, { governance_status: string; owner_count: number; owner_names: string | null }> = {};
                govData.statuses.forEach((s: { document_id: string; governance_status: string; owner_count: number; owner_names: string | null }) => {
                  statusMap[s.document_id] = { governance_status: s.governance_status, owner_count: s.owner_count, owner_names: s.owner_names };
                });
                setGovernanceStatuses(statusMap);
              }
            }
          } catch (govErr) {
            console.warn('[GovernanceDocuments] Failed to load governance statuses:', govErr);
          }
        }
      } else {
        throw new Error(data.error || 'Failed to load documents');
      }
    } catch (err: unknown) {
      console.error('[GovernanceDocuments] Load error:', err);
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  };

  // Load lens results for all documents (DC-1041)
  const loadLensResults = useCallback(async (docIds: string[]) => {
    if (docIds.length === 0) return;

    try {
      // Fetch tier1 results for each document
      const results: Record<string, LensResultSummary> = {};
      await Promise.all(
        docIds.map(async (docId) => {
          try {
            const response = await fetch(`/api/v1/governance-documents/${docId}/tier1-results`);
            if (response.ok) {
              const data = await response.json();
              if (data.success && data.result) {
                const lensCount = Object.keys(data.result.lens_results || {}).length;
                results[docId] = {
                  overall_score: data.result.overall_score,
                  lenses_run: lensCount,
                  total_lenses: 6, // Total available lenses
                  analyzed_at: data.result.analyzed_at,
                };
              }
            }
          } catch (err) {
            // Silently fail for individual documents
            console.warn(`[GovernanceDocuments] Failed to load lens results for ${docId}:`, err);
          }
        })
      );
      setLensResults(results);
    } catch (err) {
      console.warn('[GovernanceDocuments] Failed to load lens results:', err);
    }
  }, []);

  // Run lens analysis for a single document (DC-1041)
  const handleRunLenses = useCallback(async (documentId: string) => {
    try {
      setRunningLenses(prev => new Set([...prev, documentId]));
      setError(null);

      const response = await fetch(`/api/v1/governance-documents/${documentId}/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lens_ids: ['fair', 'ml_readiness', 'privacy_compliance', 'regulatory', 'data_quality', 'financial_services'],
        }),
      });

      if (!response.ok) {
        throw new Error(`Lens analysis failed: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success && data.result) {
        const lensCount = Object.keys(data.result.lens_results || {}).length;
        setLensResults(prev => ({
          ...prev,
          [documentId]: {
            overall_score: data.result.overall_score,
            lenses_run: lensCount,
            total_lenses: 6,
            analyzed_at: data.result.analyzed_at,
          },
        }));
        setSuccessMessage(`Lens analysis complete. Score: ${Math.round(data.result.overall_score)}%`);
      } else {
        throw new Error(data.error || 'Lens analysis failed');
      }
    } catch (err: unknown) {
      console.error('[GovernanceDocuments] Lens analysis error:', err);
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setRunningLenses(prev => {
        const next = new Set(prev);
        next.delete(documentId);
        return next;
      });
    }
  }, []);

  // Load lens results when documents change
  useEffect(() => {
    if (documents.length > 0) {
      const docIds = documents.map(d => d.document_id);
      loadLensResults(docIds);
    }
  }, [documents, loadLensResults]);

  // Navigate to document detail (DC-1041)
  const handleViewDocument = useCallback((documentId: string) => {
    navigate(`/settings/governance/documents/${documentId}`);
  }, [navigate]);

  // Handle file selection - with server-side PDF parsing
  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadNotice(null);
    try {
      // PDF and Word documents go through the server-side extractor
      // (pdf-parse / mammoth). v1.2.3: .docx is Teams' native transcript
      // download format — first-class, not read as raw text.
      const lower = file.name.toLowerCase();
      if (
        file.type === 'application/pdf' || lower.endsWith('.pdf') ||
        lower.endsWith('.docx') || file.type.includes('officedocument') || file.type.includes('msword')
      ) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch('/api/upload-pdf', {
          method: 'POST',
          body: formData,
          credentials: 'include', // /api/upload-pdf is auth-gated in v1.2; send the session cookie
        });

        if (response.status === 401) {
          throw new Error('Your session has expired. Please log in again before uploading the document.');
        }
        if (!response.ok) {
          // v1.2.3 — surface the server's named cause (NOT_A_PDF,
          // PDF_ENCRYPTED, FILE_TOO_LARGE, …) instead of the status text.
          const body = await response.json().catch(() => ({}));
          throw new Error(body.error?.message || body.message || `PDF upload failed: ${response.statusText}`);
        }

        const data = await response.json();

        // Check if PDF had extractable text
        if (!data.text || data.text.length < 50) {
          setError('PDF appears to be scanned or image-based. Please upload a text-based PDF or paste the content manually.');
          return;
        }

        // v2.0 (§3.7) — surface the server's extraction-quality flags. These
        // WARN but do not block (the <50-char hard block above stays).
        const notices: string[] = [];
        if (data.scannedLikely) {
          const extractedChars: number = typeof data.textLength === 'number' ? data.textLength : data.text.length;
          notices.push(
            `This document appears to be scanned or image-based — only ${extractedChars.toLocaleString()} characters of text were extracted. ` +
            'FAIR analysis will be unreliable; consider pasting the text manually.'
          );
        }
        if (data.truncated) {
          notices.push(
            typeof data.warning === 'string' && data.warning
              ? data.warning
              : 'The extracted text was truncated at the extraction cap — analysis will only cover the extracted portion.'
          );
        }
        if (notices.length > 0) {
          setUploadNotice(notices.join(' '));
        }

        setDocumentText(data.text);
      } else {
        // For text files, read directly
        const content = await file.text();
        // v1.2.3 — binary guard: Word/zip content decodes to NULs and
        // replacement chars; loading it as document text silently pollutes
        // the analysis. Fail with a descriptive error instead.
        const sample = content.slice(0, 2000);
        const junk = (sample.match(/[\u0000�]/g) || []).length;
        if (junk > 5) {
          setError(`"${file.name}" looks like a binary file, not text. Word documents are not supported — export to PDF or paste the text directly.`);
          return;
        }
        setDocumentText(content);
      }

      // Auto-fill document name from filename if not already set
      if (!documentName) {
        setDocumentName(file.name.replace(/\.[^/.]+$/, ''));
      }
    } catch (err: unknown) {
      setError('Failed to read file: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  }, [documentName]);

  // Upload document
  const handleUpload = useCallback(async () => {
    if (!documentName.trim() || !category || !documentText.trim()) {
      setError('Document name, category, and content are required');
      return;
    }
    // DC-1635 — a domain is OPTIONAL for governance documents: corp-level FAIR
    // policies/DMPs/SOPs govern every domain. A group transcript is the one
    // exception — it needs a domain so its speakers feed that domain's Perceived
    // leg (per-speaker persona extraction inherits the domain).
    if (category === 'group_transcript' && !domainId) {
      setError('Please select a domain for this group transcript.');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const response = await fetch('/api/organization/documents/upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          document_name: documentName,
          document_type: documentType,
          category,
          document_text: documentText,
          version: version || null,
          // FAIR pillar flags
          evaluate_findable: evalFindable,
          evaluate_accessible: evalAccessible,
          evaluate_interoperable: evalInteroperable,
          evaluate_reusable: evalReusable,
          // DC-1666 — opt-in to internal assessment citations (default off)
          include_in_assessment_citations: includeInCitations,
          // DC-1493 — optional triangulation domain
          domain_id: domainId,
        }),
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success) {
        // Reset form and reload
        setDocumentName('');
        setDocumentType('policy');
        setCategory('');
        setDocumentText('');
        setVersion('');
        setEvalFindable(false);
        setEvalAccessible(false);
        setEvalInteroperable(false);
        setEvalReusable(false);
        setIncludeInCitations(false);
        setDomainId(null);
        setUploadNotice(null);
        setShowUploadForm(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
        // DC-1603: surface group-transcript ingestion results instead of the
        // generic recommendations line. A failed per-speaker NLP run used to be
        // invisible here — now the speaker/analyzed counts and any warnings are
        // shown, and an un-analyzed remainder points the consultant at the
        // per-speaker "Analyze" affordance.
        const gti = data.group_transcript_ingestion as
          | { speaker_count: number; analyzed_count: number; warnings: string[] }
          | undefined;
        if (gti) {
          const plural = gti.speaker_count === 1 ? '' : 's';
          let message = `Document uploaded. Parsed ${gti.speaker_count} speaker${plural}, ${gti.analyzed_count} analyzed. Open the document to map speakers to personas in the Speaker → Persona Mapping section.`;
          if (gti.warnings.length > 0) {
            message += ` ⚠ ${gti.warnings.join(' ')}`;
          } else if (gti.analyzed_count < gti.speaker_count) {
            const remaining = gti.speaker_count - gti.analyzed_count;
            message += ` ${remaining} still pending — open the document and use "Analyze" in that section.`;
          }
          setSuccessMessage(message);
        } else {
          setSuccessMessage(`Document uploaded. ${data.recommendations_generated || 0} recommendations generated.`);
        }
        await loadDocuments();
      } else {
        throw new Error(data.error || 'Upload failed');
      }
    } catch (err: unknown) {
      console.error('[GovernanceDocuments] Upload error:', err);
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setUploading(false);
    }
  }, [documentName, documentType, category, documentText, version, evalFindable, evalAccessible, evalInteroperable, evalReusable, includeInCitations, domainId]);

  // Multi-select handlers
  const toggleSelectDocument = useCallback((documentId: string) => {
    setSelectedDocuments(prev => {
      const next = new Set(prev);
      if (next.has(documentId)) {
        next.delete(documentId);
      } else {
        next.add(documentId);
      }
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectedDocuments.size === documents.length) {
      setSelectedDocuments(new Set());
    } else {
      setSelectedDocuments(new Set(documents.map(d => d.document_id)));
    }
  }, [documents, selectedDocuments.size]);

  // Batch analyze selected documents
  const handleAnalyzeSelected = useCallback(async () => {
    if (selectedDocuments.size === 0) {
      setError('Please select at least one document to analyze');
      return;
    }

    try {
      setAnalyzing(true);
      setError(null);

      const response = await fetch('/api/organization/documents/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          document_ids: Array.from(selectedDocuments),
        }),
      });

      if (!response.ok) {
        throw new Error(`Analysis failed: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success) {
        setSuccessMessage(
          `Analyzed ${data.metadata?.document_count || selectedDocuments.size} documents. ` +
          `${data.recommendations_generated || 0} recommendations generated. ` +
          `Governance Maturity: ${data.governance_maturity_score}%`
        );
        setSelectedDocuments(new Set());
        await loadDocuments();
      } else {
        throw new Error(data.error || 'Analysis failed');
      }
    } catch (err: unknown) {
      console.error('[GovernanceDocuments] Analyze error:', err);
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setAnalyzing(false);
    }
  }, [selectedDocuments]);

  // Delete document
  // DC-1666 (v2.5) — flip a document's internal-citation opt-in from the list.
  const handleToggleCitationOptIn = useCallback(async (documentId: string, next: boolean) => {
    try {
      const response = await fetch(`/api/organization/documents/${documentId}/citation-opt-in`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ include_in_assessment_citations: next }),
      });
      if (!response.ok) {
        throw new Error(`Update failed: ${response.statusText}`);
      }
      await loadDocuments();
    } catch (err: unknown) {
      console.error('[GovernanceDocuments] Citation opt-in error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
  }, []);

  const handleDelete = useCallback(async (documentId: string) => {
    if (!confirm('Delete this document?')) return;

    try {
      const response = await fetch(`/api/organization/documents/${documentId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`Delete failed: ${response.statusText}`);
      }

      await loadDocuments();
    } catch (err: unknown) {
      console.error('[GovernanceDocuments] Delete error:', err);
      setError(err instanceof Error ? err.message : "Unknown error");
    }
  }, []);

  // Export to Excel
  const handleExport = useCallback(async () => {
    try {
      setExporting(true);
      setError(null);

      // Build URL with optional document_ids filter
      let url = '/api/organization/documents/export.xlsx';
      if (selectedDocuments.size > 0) {
        const ids = Array.from(selectedDocuments).join(',');
        url += `?document_ids=${encodeURIComponent(ids)}`;
      }

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
      }

      // Get the blob and trigger download
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `governance_documents_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      setSuccessMessage(`Exported ${selectedDocuments.size > 0 ? selectedDocuments.size : documents.length} documents to Excel`);
    } catch (err: unknown) {
      console.error('[GovernanceDocuments] Export error:', err);
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setExporting(false);
    }
  }, [selectedDocuments, documents.length]);

  // Get maturity level color (cream/teal tokens, v2.0 beat 11)
  const _getMaturityColor = (level: string): string => {
    const colors: Record<string, string> = {
      Optimized: 'var(--tri-good)',
      Measured: 'var(--tri-teal)',
      Defined: 'var(--tri-soft)',
      Managed: 'var(--tri-terra)',
      Initial: 'var(--tri-hard)',
    };
    return colors[level] || 'var(--tri-slate)';
  };

  // Get color for FAIR score percentage
  const getFairScoreColor = (percentage: number): string => {
    if (percentage >= 80) return 'var(--tri-good)';
    if (percentage >= 60) return 'var(--tri-teal)';
    if (percentage >= 40) return 'var(--tri-soft)';
    return 'var(--tri-hard)';
  };

  // Get completeness badge
  const getCompletenessBadge = (status: string | null) => {
    if (!status) return { label: 'Not Analyzed', class: 'badge--pending' };
    const badges: Record<string, { label: string; class: string }> = {
      complete: { label: 'Complete', class: 'badge--success' },
      partial: { label: 'Partial', class: 'badge--warning' },
      missing: { label: 'Missing', class: 'badge--error' },
    };
    return badges[status] || { label: status, class: 'badge--pending' };
  };

  // Get category info
  const getCategoryInfo = (categoryId: string) => {
    return DOCUMENT_CATEGORIES.find(c => c.id === categoryId) || { id: categoryId, label: categoryId, icon: '?' };
  };

  if (loading) {
    return (
      <div className="governance-loading">
        <div className="spinner" />
        <p>Loading governance documents...</p>
      </div>
    );
  }

  return (
    <div className="governance-section governance-documents">
      {/* Header */}
      <div className="governance-section__header">
        <div className="governance-section__title">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <h2>Governance Documents</h2>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleOpenGuide();
              }}
              className="help-link"
              title="Open Governance Documents User Guide"
              style={{
                fontSize: '1.25rem',
                textDecoration: 'none',
                color: 'var(--tri-teal)',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                backgroundColor: 'var(--tri-teal-tint)',
                border: '1px solid var(--tri-teal-tint-line)',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--tri-teal-tint-line)';
                e.currentTarget.style.borderColor = 'var(--tri-teal)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--tri-teal-tint)';
                e.currentTarget.style.borderColor = 'var(--tri-teal-tint-line)';
              }}
            >
              ?
            </a>
          </div>
          <p className="governance-section__subtitle">
            Upload policy documents to measure Governance Maturity
          </p>
        </div>
        <div className="governance-section__actions">
          {selectedDocuments.size > 0 && (
            <button
              onClick={handleAnalyzeSelected}
              disabled={analyzing}
              className="btn btn--success"
            >
              {analyzing ? 'Analyzing...' : `Analyze Selected (${selectedDocuments.size})`}
            </button>
          )}
          {documents.length > 0 && (
            <button
              onClick={handleExport}
              disabled={exporting}
              className="btn btn--secondary"
              title={selectedDocuments.size > 0 ? `Export ${selectedDocuments.size} selected` : 'Export all documents'}
            >
              {exporting ? 'Exporting...' : (selectedDocuments.size > 0 ? `Export (${selectedDocuments.size})` : 'Export Excel')}
            </button>
          )}
          <button
            onClick={() => setShowUploadForm(!showUploadForm)}
            className="btn btn--primary"
          >
            + Upload Document
          </button>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="governance-success">
          <span className="governance-success__icon">OK</span>
          <span className="governance-success__message">{successMessage}</span>
          <button onClick={() => setSuccessMessage(null)} className="governance-success__dismiss">
            X
          </button>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="governance-error">
          <span className="governance-error__icon">!</span>
          <span className="governance-error__message">{error}</span>
          <button onClick={() => setError(null)} className="governance-error__dismiss">
            X
          </button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="governance-summary">
        <div className="summary-card">
          <div className="summary-card__value">{aggregate?.total_documents || 0}</div>
          <div className="summary-card__label">Documents</div>
        </div>
        <div className="summary-card">
          <div className="summary-card__value">{aggregate?.analyzed_documents || 0}</div>
          <div className="summary-card__label">Analyzed</div>
        </div>
        <div className="summary-card summary-card--primary">
          <div className="summary-card__value">
            {aggregate?.fair_score != null
              ? `${aggregate.fair_score.toFixed(1)}/5`
              : '--'}
          </div>
          <div className="summary-card__label">FAIR Score</div>
        </div>
        <div className="summary-card">
          <div
            className="summary-card__value"
            style={{ color: getFairScoreColor(aggregate?.fair_percentage || 0) }}
          >
            {aggregate?.fair_percentage != null ? `${aggregate.fair_percentage}%` : '--'}
          </div>
          <div className="summary-card__label">Coverage</div>
        </div>
      </div>

      {/* FAIR Sub-Rule Coverage */}
      {aggregate?.fair_coverage && aggregate.fair_coverage.length > 0 && (
        <div className="fair-coverage-section">
          <h4>FAIR Sub-Rule Coverage</h4>
          <div className="fair-coverage-grid">
            {['F', 'A', 'I', 'R'].map(pillar => {
              const pillarRules = aggregate.fair_coverage.filter(r => r.pillar === pillar);
              const pillarSelected = aggregate.selected_pillars?.[pillar as keyof typeof aggregate.selected_pillars];
              const pillarName = pillar === 'F' ? 'Findable' : pillar === 'A' ? 'Accessible' : pillar === 'I' ? 'Interoperable' : 'Reusable';

              return (
                <div key={pillar} className={`fair-pillar-group ${!pillarSelected ? 'fair-pillar-group--disabled' : ''}`}>
                  <div className="fair-pillar-header">
                    <span className={`fair-pillar-badge fair-pillar-badge--${pillar.toLowerCase()}`}>{pillar}</span>
                    <span className="fair-pillar-name">{pillarName}</span>
                    {!pillarSelected && <span className="fair-pillar-disabled">(not evaluated)</span>}
                  </div>
                  <div className="fair-rules-list">
                    {pillarRules.map(rule => (
                      <div key={rule.id} className={`fair-rule-item fair-rule-item--${rule.status}`} title={rule.description}>
                        <span className="fair-rule-status">
                          {rule.status === 'covered' ? '✓' : rule.status === 'partial' ? '◐' : rule.status === 'missing' ? '✗' : '○'}
                        </span>
                        <span className="fair-rule-id">{rule.id}</span>
                        <span className="fair-rule-name">{rule.name}</span>
                        {rule.evidence_count > 0 && (
                          <span className="fair-rule-evidence">({rule.accepted_count}/{rule.evidence_count})</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Upload Form */}
      {showUploadForm && (
        <div className="governance-upload-form">
          <h3>Upload Governance Document</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Document Name *</label>
              <input
                type="text"
                value={documentName}
                onChange={e => setDocumentName(e.target.value)}
                placeholder="e.g., Data Quality Policy v2.1"
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Document Type</label>
              <select
                value={documentType}
                onChange={e => setDocumentType(e.target.value)}
                className="form-input"
              >
                {DOCUMENT_TYPES.map(dt => (
                  <option key={dt.id} value={dt.id}>{dt.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Category *</label>
              <select
                value={category}
                onChange={e => setCategory(e.target.value)}
                className="form-input"
              >
                <option value="">Select category...</option>
                {DOCUMENT_CATEGORIES.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    [{cat.icon}] {cat.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Version (optional)</label>
              <input
                type="text"
                value={version}
                onChange={e => setVersion(e.target.value)}
                placeholder="e.g., 2.1"
                className="form-input"
              />
            </div>
            <div className="form-group form-group--full">
              <label>FAIR Pillars to Evaluate</label>
              <span className="form-hint">Auto-selected based on category. Modify as needed.</span>
              <div className="fair-pillar-checkboxes">
                <label className="pillar-checkbox">
                  <input
                    type="checkbox"
                    checked={evalFindable}
                    onChange={e => setEvalFindable(e.target.checked)}
                  />
                  <span className="pillar-label pillar-F">F</span> Findable
                </label>
                <label className="pillar-checkbox">
                  <input
                    type="checkbox"
                    checked={evalAccessible}
                    onChange={e => setEvalAccessible(e.target.checked)}
                  />
                  <span className="pillar-label pillar-A">A</span> Accessible
                </label>
                <label className="pillar-checkbox">
                  <input
                    type="checkbox"
                    checked={evalInteroperable}
                    onChange={e => setEvalInteroperable(e.target.checked)}
                  />
                  <span className="pillar-label pillar-I">I</span> Interoperable
                </label>
                <label className="pillar-checkbox">
                  <input
                    type="checkbox"
                    checked={evalReusable}
                    onChange={e => setEvalReusable(e.target.checked)}
                  />
                  <span className="pillar-label pillar-R">R</span> Reusable
                </label>
              </div>
            </div>
            <div className="form-group form-group--full">
              <label>Internal Governance Citations</label>
              <span className="form-hint">
                Opt this document in to cite its own clauses beside Data Quality and ML Readiness
                scores. Suggested mappings are reviewed and approved before anything appears. Off by default.
              </span>
              <label className="pillar-checkbox">
                <input
                  type="checkbox"
                  checked={includeInCitations}
                  onChange={e => setIncludeInCitations(e.target.checked)}
                />
                Use this document as an internal citation source
              </label>
            </div>
            <div className="form-group form-group--full">
              <DomainSelect
                required={category === 'group_transcript'}
                value={domainId}
                onChange={setDomainId}
                helpText={
                  category === 'group_transcript'
                    ? "Required for group transcripts — the domain whose people were interviewed; it feeds that domain's Perceived leg via per-speaker persona extraction."
                    : "Optional. Leave as “No domain” for a corp-level FAIR policy/SOP/DMP that governs the whole organization — it contributes to every domain's Governed leg. Or pick one domain to scope it."
                }
              />
            </div>
            <div className="form-group">
              <label>Upload Document File</label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".txt,.md,.vtt,.pdf,.docx,application/pdf"
                onChange={handleFileSelect}
                className="form-input"
              />
            </div>
          </div>
          {/* v2.0 (§3.7) — non-blocking extraction-quality warning. Mirrors the
              .governance-error banner pattern, restyled with the soft (warning)
              tokens since no CSS change is in scope for this view. */}
          {uploadNotice && (
            <div
              className="governance-error"
              style={{ background: 'var(--tri-soft-bg)', borderColor: 'var(--tri-soft)' }}
            >
              <span className="governance-error__icon" style={{ color: 'var(--tri-soft)' }}>!</span>
              <span className="governance-error__message" style={{ color: 'var(--tri-soft)' }}>{uploadNotice}</span>
              <button
                onClick={() => setUploadNotice(null)}
                className="governance-error__dismiss"
                style={{ color: 'var(--tri-soft)' }}
              >
                X
              </button>
            </div>
          )}
          <div className="form-group form-group--full">
            <label>Document Content *</label>
            <textarea
              value={documentText}
              onChange={e => setDocumentText(e.target.value)}
              placeholder="Paste the document content here, or upload a file above..."
              rows={10}
              className="form-textarea"
            />
          </div>
          <div className="form-actions">
            <button
              onClick={handleUpload}
              disabled={uploading || !documentName.trim() || !category || !documentText.trim()}
              className="btn btn--primary"
            >
              {uploading ? 'Uploading...' : 'Upload Document'}
            </button>
            <button
              onClick={() => setShowUploadForm(false)}
              className="btn btn--secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Document List */}
      <div className="governance-list">
        <div className="list-header">
          <h3>Uploaded Documents</h3>
          <span className="list-count">{documents.length} document{documents.length !== 1 ? 's' : ''}</span>
        </div>
        {documents.length === 0 ? (
          <div className="governance-empty">
            <p>No governance documents uploaded yet.</p>
            <p className="governance-empty__hint">
              Click "Upload Document" to add your organization's data governance policies.
              Documents are analyzed for maturity to identify gaps in your governance framework.
            </p>
          </div>
        ) : (
          <table className="governance-table governance-table--selectable">
            <thead>
              <tr>
                <th className="th-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedDocuments.size === documents.length && documents.length > 0}
                    onChange={toggleSelectAll}
                    title="Select all"
                  />
                </th>
                <th>Document</th>
                <th>Category</th>
                <th className="th-lens-score" title="Multi-lens analysis score">Lens Score</th>
                <th className="th-lenses-run" title="Number of lenses analyzed">Lenses</th>
                <th className="th-owner" title="Document Owner (click to assign)">Owner</th>
                <th>FAIR</th>
                <th>Maturity</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {documents.map(doc => {
                const catInfo = getCategoryInfo(doc.category);
                const badge = getCompletenessBadge(doc.completeness_status);
                const isSelected = selectedDocuments.has(doc.document_id);
                const pillars = doc.fair_pillars || { findable: false, accessible: false, interoperable: false, reusable: false };
                const govStatus = governanceStatuses[doc.document_id];
                const hasOwner = govStatus?.governance_status === 'complete';
                const lensResult = lensResults[doc.document_id];
                const isRunningLens = runningLenses.has(doc.document_id);
                return (
                  <tr
                    key={doc.document_id}
                    className={isSelected ? 'row-selected' : ''}
                  >
                    <td className="td-checkbox">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelectDocument(doc.document_id)}
                      />
                    </td>
                    <td>
                      <button
                        className="doc-name-button"
                        onClick={() => handleViewDocument(doc.document_id)}
                        title="Click to view lens analysis details"
                      >
                        <strong>{doc.document_name}</strong>
                        {doc.version && <span className="version-tag">v{doc.version}</span>}
                      </button>
                    </td>
                    <td>
                      <span className="category-badge">
                        [{catInfo.icon}] {catInfo.label}
                      </span>
                    </td>
                    <td className="td-lens-score">
                      {isRunningLens ? (
                        <div className="lens-loading">
                          <span className="lens-spinner" />
                        </div>
                      ) : lensResult?.overall_score != null ? (
                        <div className="lens-score-mini" title={`Analyzed: ${new Date(lensResult.analyzed_at || '').toLocaleDateString()}`}>
                          <ScoreGauge score={lensResult.overall_score} size="sm" />
                        </div>
                      ) : (
                        <span className="lens-score-empty">--</span>
                      )}
                    </td>
                    <td className="td-lenses-run">
                      {lensResult ? (
                        <span className="lenses-count">
                          {lensResult.lenses_run}/{lensResult.total_lenses}
                        </span>
                      ) : (
                        <span className="lenses-count lenses-count--empty">0/6</span>
                      )}
                    </td>
                    <td className="td-owner">
                      <button
                        className={`owner-indicator ${hasOwner ? 'owner-indicator--assigned' : 'owner-indicator--missing'}`}
                        onClick={() => setSelectedDocumentForPanel(doc)}
                        title={hasOwner ? `Owner: ${govStatus.owner_names}` : 'No owner assigned - click to assign'}
                      >
                        {hasOwner ? '●' : '○'}
                      </button>
                    </td>
                    <td>
                      <div className="fair-pillar-indicators">
                        <span className={`pillar-indicator ${pillars.findable ? 'pillar-active pillar-F' : 'pillar-inactive'}`} title="Findable">F</span>
                        <span className={`pillar-indicator ${pillars.accessible ? 'pillar-active pillar-A' : 'pillar-inactive'}`} title="Accessible">A</span>
                        <span className={`pillar-indicator ${pillars.interoperable ? 'pillar-active pillar-I' : 'pillar-inactive'}`} title="Interoperable">I</span>
                        <span className={`pillar-indicator ${pillars.reusable ? 'pillar-active pillar-R' : 'pillar-inactive'}`} title="Reusable">R</span>
                      </div>
                    </td>
                    <td>
                      {doc.maturity_score != null ? (
                        <div className="maturity-score">
                          <div className="maturity-bar">
                            <div
                              className="maturity-bar__fill"
                              style={{ width: `${doc.maturity_score}%` }}
                            />
                          </div>
                          <span>{Math.round(doc.maturity_score)}%</span>
                        </div>
                      ) : (
                        '--'
                      )}
                    </td>
                    <td>
                      <span className={`status-badge ${badge.class}`}>
                        {badge.label}
                      </span>
                    </td>
                    <td className="td-actions">
                      <div className="action-buttons">
                        <button
                          onClick={() => handleRunLenses(doc.document_id)}
                          disabled={isRunningLens}
                          className="btn btn--small btn--primary"
                          title="Run multi-lens analysis"
                        >
                          {isRunningLens ? 'Running...' : 'Run Lenses'}
                        </button>
                        <button
                          onClick={() => handleViewDocument(doc.document_id)}
                          className="btn btn--small btn--secondary"
                          title="View lens analysis details"
                        >
                          View
                        </button>
                        <button
                          onClick={() => handleToggleCitationOptIn(doc.document_id, !doc.include_in_assessment_citations)}
                          className={`btn btn--small ${doc.include_in_assessment_citations ? 'btn--primary' : 'btn--secondary'}`}
                          title={doc.include_in_assessment_citations
                            ? 'Opted in as an internal citation source — click to opt out'
                            : 'Opt this document in as an internal citation source'}
                          aria-pressed={doc.include_in_assessment_citations ? true : false}
                        >
                          {doc.include_in_assessment_citations ? 'Cited ✓' : 'Cite'}
                        </button>
                        <button
                          onClick={() => handleDelete(doc.document_id)}
                          className="btn btn--small btn--danger"
                          title="Delete document"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Help Section */}
      <div className="governance-help">
        <h4>About Governance Maturity</h4>
        <p>
          Governance Maturity measures the completeness and quality of your organization's
          data governance policies. It contributes <strong>35%</strong> to the Execution Risk coefficient.
        </p>
        <p>
          Upload policy documents covering Data Quality, Data Access, Data Lifecycle, Metadata Standards,
          Privacy, Retention, and Security. The system identifies gaps and scores maturity based on
          content analysis.
        </p>
      </div>

      {/* Document Detail Panel (slide-out) */}
      {selectedDocumentForPanel && (
        <DocumentDetailPanel
          document={selectedDocumentForPanel}
          onClose={() => setSelectedDocumentForPanel(null)}
          onOwnerChange={() => {
            // Refresh governance statuses
            loadDocuments();
          }}
        />
      )}
    </div>
  );
};

export default GovernanceDocuments;
